import { useEffect, useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import {
  ArrowRight, Send, Users, User, Upload, Loader2, X,
  Image, ChevronDown, Check, Search, History, Eye, EyeOff, Mail, UserX,
} from 'lucide-react';
import { toast } from 'sonner';
import { EmailQueueCard } from '../components/EmailQueueCard';

const ReaderDropdown = ({ label, readers, selected, setSelected, open, setOpen }) => {
  const ref = useRef(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [setOpen]);

  const filtered = readers.filter((r) => !search || r.name.includes(search) || r.reader_number.includes(search));
  const allSelected = readers.length > 0 && selected.length === readers.length;

  const toggle = (rn) => {
    setSelected((prev) => prev.includes(rn) ? prev.filter((x) => x !== rn) : [...prev, rn]);
  };

  const toggleAll = () => {
    if (allSelected) setSelected([]);
    else setSelected(readers.map((r) => r.reader_number));
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`w-full flex items-center justify-between gap-2 p-3 rounded-xl border-2 transition-all text-sm font-medium ${
          selected.length > 0 ? 'border-primary bg-primary/10 text-primary' : 'border-gray-200 text-gray-600 hover:border-gray-300'
        }`}
        data-testid={`target-${label}`}
      >
        <div className="flex items-center gap-2">
          <User className="w-4 h-4" />
          <span>{label}</span>
          {selected.length > 0 && <span className="bg-primary text-white text-xs px-2 py-0.5 rounded-full">{selected.length}</span>}
        </div>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-50 mt-2 w-full bg-white rounded-xl border-2 border-gray-200 shadow-xl max-h-[350px] overflow-hidden" data-testid={`dropdown-${label}`}>
          {/* Search */}
          <div className="p-2 border-b sticky top-0 bg-white z-10">
            <div className="relative">
              <Search className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="بحث..."
                className="w-full pr-8 pl-3 py-2 text-sm border rounded-lg focus:outline-none focus:border-primary"
              />
            </div>
          </div>

          {/* Select All */}
          <button type="button" onClick={toggleAll} className="w-full flex items-center gap-3 px-4 py-3 text-sm hover:bg-primary/5 border-b transition-colors">
            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${allSelected ? 'bg-primary border-primary' : 'border-gray-300'}`}>
              {allSelected && <Check className="w-3 h-3 text-white" />}
            </div>
            <span className="font-semibold text-primary">تحديد الكل ({readers.length})</span>
          </button>

          {/* List */}
          <div className="overflow-y-auto max-h-[250px]">
            {filtered.map((r) => {
              const isChecked = selected.includes(r.reader_number);
              return (
                <button key={r.reader_number} type="button" onClick={() => toggle(r.reader_number)} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors">
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${isChecked ? 'bg-primary border-primary' : 'border-gray-300'}`}>
                    {isChecked && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <span className="flex-1 text-right truncate">{r.name}</span>
                  <span className="text-xs text-muted-foreground shrink-0 font-mono">{r.reader_number}</span>
                </button>
              );
            })}
            {filtered.length === 0 && <p className="text-center text-sm text-muted-foreground py-4">لا توجد نتائج</p>}
          </div>
        </div>
      )}
    </div>
  );
};

const AdminNotificationsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [form, setForm] = useState({ title: '', message: '', image_url: '', manualReaders: '' });
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [allSelected, setAllSelected] = useState(false);
  const [selectedMales, setSelectedMales] = useState([]);
  const [selectedFemales, setSelectedFemales] = useState([]);
  const [maleOpen, setMaleOpen] = useState(false);
  const [femaleOpen, setFemaleOpen] = useState(false);
  const [readers, setReaders] = useState({ males: [], females: [] });
  const [log, setLog] = useState([]);
  const [loadingLog, setLoadingLog] = useState(true);
  const [inactiveDays, setInactiveDays] = useState(14);
  const [loadingInactive, setLoadingInactive] = useState(false);
  const [inactiveCount, setInactiveCount] = useState(0);
  const [testEmail, setTestEmail] = useState('');
  const [testingEmail, setTestingEmail] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    if (!user?.is_admin || user?.admin_role === 'محرر') { navigate('/admin'); return; }
    api.get('/admin/readers-list').then((r) => setReaders(r.data)).catch(() => {});
    loadLog();
  }, []);

  const loadLog = async () => {
    try { const res = await api.get('/admin/notifications/log'); setLog(res.data); }
    catch {} finally { setLoadingLog(false); }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/upload/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm((prev) => ({ ...prev, image_url: `${process.env.REACT_APP_BACKEND_URL}/api/files/${res.data.path}` }));
      toast.success('تم رفع الصورة');
    } catch { toast.error('فشل الرفع'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const handleAllToggle = () => {
    if (allSelected) {
      setAllSelected(false);
      setSelectedMales([]);
      setSelectedFemales([]);
    } else {
      setAllSelected(true);
      setSelectedMales(readers.males.map((r) => r.reader_number));
      setSelectedFemales(readers.females.map((r) => r.reader_number));
    }
    setInactiveCount(0);
  };

  /** Pre-select the recipient list with inactive-uncontacted readers.
   *  Uses `/admin/inactive-readers?only_uncontacted=true` so admins can nudge
   *  those who neither reported recently nor were already contacted. Emails
   *  are handled server-side by the send-notification flow (via sheet lookup). */
  const handleTargetInactive = async () => {
    setLoadingInactive(true);
    try {
      const { data } = await api.get('/admin/inactive-readers', {
        params: { days: inactiveDays, gender: 'all', only_uncontacted: true },
      });
      const items = data?.items || [];
      if (items.length === 0) {
        toast.info('لا توجد حسابات خاملة غير متواصل معها');
        setInactiveCount(0);
        return;
      }
      // Split by gender so the existing summary chips stay accurate.
      const maleNums = items.filter(x => x.gender !== 'أنثى').map(x => x.reader_number);
      const femaleNums = items.filter(x => x.gender === 'أنثى').map(x => x.reader_number);
      setAllSelected(false);
      setSelectedMales(maleNums);
      setSelectedFemales(femaleNums);
      setInactiveCount(items.length);
      toast.success(`تم تحديد ${items.length} حساباً خاملاً غير متواصل معه`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل جلب الحسابات الخاملة');
    } finally {
      setLoadingInactive(false);
    }
  };

  const handleSend = async () => {
    if (!form.title || !form.message) { toast.error('العنوان والرسالة مطلوبان'); return; }

    const manual = form.manualReaders.split(/[,،\s]+/).filter(Boolean);
    const combined = [...new Set([...selectedMales, ...selectedFemales, ...manual])];

    if (combined.length === 0 && !allSelected) { toast.error('يرجى اختيار المستلمين'); return; }

    setSending(true);
    try {
      const payload = {
        title: form.title,
        message: form.message,
        image_url: form.image_url,
        target_type: allSelected ? 'all' : 'specific',
        target_readers: allSelected ? [] : combined,
      };
      const res = await api.post('/admin/notifications/send', payload);
      toast.success(res.data.message);
      setForm({ title: '', message: '', image_url: '', manualReaders: '' });
      setAllSelected(false);
      setSelectedMales([]);
      setSelectedFemales([]);
      setInactiveCount(0);
      loadLog();
    } catch (err) { toast.error(err.response?.data?.detail || 'فشل الإرسال'); }
    finally { setSending(false); }
  };

  const totalSelected = allSelected
    ? (readers.males.length + readers.females.length)
    : new Set([...selectedMales, ...selectedFemales, ...form.manualReaders.split(/[,،\s]+/).filter(Boolean)]).size;

  return (
    <div className="space-y-8" data-testid="admin-notifications-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/admin')}><ArrowRight className="w-5 h-5" /></Button>
        <div>
          <h1 className="text-4xl font-bold text-accent">إرسال إشعار</h1>
          <p className="text-muted-foreground text-lg mt-1">يصل للوارد + الإيميل (إن وُجد)</p>
        </div>
      </div>

      <EmailQueueCard />

      <Card className="p-8 max-w-2xl mx-auto space-y-6">
        <div>
          <label className="block text-sm font-semibold mb-2">العنوان *</label>
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="عنوان الإشعار..." className="h-12 text-lg" data-testid="notif-title" />
        </div>
        <div>
          <label className="block text-sm font-semibold mb-2">الرسالة *</label>
          <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="نص الإشعار..." className="w-full border-2 rounded-xl p-4 min-h-[120px] resize-y focus:outline-none focus:border-primary" dir="rtl" data-testid="notif-message" />
        </div>

        {/* Image */}
        <div>
          <label className="block text-sm font-semibold mb-2">صورة مرفقة (اختياري)</label>
          {form.image_url ? (
            <div className="relative w-full h-36 rounded-xl overflow-hidden bg-muted">
              <img src={form.image_url} alt="" className="w-full h-full object-cover" />
              <button onClick={() => setForm({ ...form, image_url: '' })} className="absolute top-2 left-2 bg-white rounded-full p-1 shadow"><X className="w-4 h-4" /></button>
            </div>
          ) : (
            <div onClick={() => fileRef.current?.click()} className="border-2 border-dashed rounded-xl p-5 text-center cursor-pointer hover:border-primary/50 transition-colors">
              {uploading ? <Loader2 className="w-7 h-7 animate-spin mx-auto text-primary" /> : <><Image className="w-7 h-7 text-gray-400 mx-auto mb-1" /><p className="text-sm text-muted-foreground">اضغط لإرفاق صورة</p></>}
            </div>
          )}
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
        </div>

        {/* Target Selection */}
        <div>
          <label className="block text-sm font-semibold mb-3">المستلمون {totalSelected > 0 && <span className="text-primary">({totalSelected})</span>}</label>
          <div className="space-y-3">
            {/* Diagnostic: test email delivery via the Google Apps Script webhook */}
            <div className="rounded-xl border-2 border-blue-200 bg-blue-50/40 p-3 space-y-2">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-blue-700" />
                <span className="text-sm font-semibold text-blue-800">اختبار توصيل الإيميل</span>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="email"
                  placeholder="أدخل بريداً حقيقياً للاختبار"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  className="flex-1 h-10 rounded-lg border-2 border-blue-200 bg-white px-3 text-sm outline-none focus:border-blue-400"
                  data-testid="test-email-input"
                />
                <button
                  type="button"
                  disabled={testingEmail || !testEmail.includes('@')}
                  onClick={async () => {
                    setTestingEmail(true); setTestResult(null);
                    try {
                      const res = await api.post('/admin/notifications/test-email', { email: testEmail });
                      setTestResult(res.data);
                    } catch (err) {
                      setTestResult({ error: err.response?.data?.detail || 'فشل الاتصال' });
                    } finally { setTestingEmail(false); }
                  }}
                  className="h-10 px-4 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-60"
                  data-testid="test-email-btn"
                >
                  {testingEmail ? '...جارٍ' : 'اختبر الآن'}
                </button>
              </div>
              {testResult && (
                <div className="text-xs bg-white border border-blue-100 rounded-lg p-2 space-y-1" data-testid="test-email-result">
                  {testResult.error ? (
                    <p className="text-red-600 font-semibold">{testResult.error}</p>
                  ) : (
                    <>
                      <p><span className="font-bold">HTTP:</span> {testResult.http_status}</p>
                      <p><span className="font-bold">أُرسل:</span> {testResult.sent ?? '—'} / <span className="font-bold">فشل:</span> {testResult.failed ?? '—'}</p>
                      <pre className="text-[10px] overflow-x-auto max-w-full text-gray-700">{typeof testResult.webhook_response === 'string' ? testResult.webhook_response : JSON.stringify(testResult.webhook_response, null, 2)}</pre>
                    </>
                  )}
                </div>
              )}
              <p className="text-[11px] text-blue-800/80 leading-relaxed">
                إذا ظهر <code>sent: 0, failed: 1</code> فمعناه أن السكربت وصل ولكن Google رفض الإرسال (تجاوز الحصة، أو رفض المستلم). راجع Apps Script &gt; Executions.
              </p>
            </div>

            {/* All button */}
            <button
              type="button"
              onClick={handleAllToggle}
              className={`w-full flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all text-sm font-semibold ${
                allSelected ? 'border-primary bg-primary text-white' : 'border-gray-200 text-gray-600 hover:border-primary/30'
              }`}
              data-testid="target-all"
            >
              <Users className="w-5 h-5" /> جميع المشتركين
            </button>

            {/* Inactive uncontacted target — one click to nudge dormant readers */}
            <div
              className={`rounded-xl border-2 transition-all ${
                inactiveCount > 0 ? 'border-amber-500 bg-amber-50' : 'border-amber-200 bg-amber-50/40'
              }`}
              data-testid="target-inactive-wrap"
            >
              <div className="flex items-stretch gap-2 p-2">
                <button
                  type="button"
                  onClick={handleTargetInactive}
                  disabled={loadingInactive}
                  className="flex-1 flex items-center justify-center gap-2 h-11 rounded-lg text-sm font-semibold bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-70 transition-colors"
                  data-testid="target-inactive-btn"
                  title="يشمل من لم يقدّم تقريراً خلال المدة المحددة ولم يسبق التواصل معه"
                >
                  {loadingInactive ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserX className="w-4 h-4" />}
                  <span>الحسابات الخاملة (غير المتواصل معهم)</span>
                  {inactiveCount > 0 && (
                    <span className="bg-white text-amber-700 text-xs px-2 py-0.5 rounded-full font-bold">{inactiveCount}</span>
                  )}
                </button>
                <div className="flex items-center gap-1 bg-white border border-amber-200 rounded-lg px-2">
                  <label className="text-[11px] text-amber-800 whitespace-nowrap">منذ (يوماً)</label>
                  <input
                    type="number"
                    min="1"
                    max="365"
                    value={inactiveDays}
                    onChange={(e) => setInactiveDays(Math.max(1, Number(e.target.value) || 14))}
                    className="w-14 h-8 text-center text-sm border-0 focus:outline-none"
                    data-testid="target-inactive-days"
                  />
                </div>
              </div>
              <p className="text-[11px] text-amber-800/80 px-3 pb-2 leading-relaxed">
                عند الضغط سيُحدَّد كل من مضى على آخر تقرير له {inactiveDays} يوماً فأكثر ولم تُؤشِّر عليه بأنه تم التواصل معه.
                الإشعار سيصل لواردهم وإيميلاتهم في نفس الوقت.
              </p>
            </div>

            {/* Male & Female dropdowns */}
            <div className="grid grid-cols-2 gap-3">
              <ReaderDropdown
                label="القراء"
                readers={readers.males}
                selected={selectedMales}
                setSelected={(val) => { setSelectedMales(typeof val === 'function' ? val(selectedMales) : val); setAllSelected(false); setInactiveCount(0); }}
                open={maleOpen}
                setOpen={(v) => { setMaleOpen(v); if (v) setFemaleOpen(false); }}
              />
              <ReaderDropdown
                label="القارئات"
                readers={readers.females}
                selected={selectedFemales}
                setSelected={(val) => { setSelectedFemales(typeof val === 'function' ? val(selectedFemales) : val); setAllSelected(false); setInactiveCount(0); }}
                open={femaleOpen}
                setOpen={(v) => { setFemaleOpen(v); if (v) setMaleOpen(false); }}
              />
            </div>

            {/* Manual input */}
            <div>
              <label className="block text-xs text-muted-foreground mb-1">أو أدخل أرقاماً مباشرة (مفصولة بفاصلة)</label>
              <Input
                value={form.manualReaders}
                onChange={(e) => setForm({ ...form, manualReaders: e.target.value })}
                placeholder="92343, 94032, ..."
                className="text-left font-mono"
                dir="ltr"
                data-testid="manual-readers"
              />
            </div>
          </div>
        </div>

        <Button onClick={handleSend} disabled={sending} className="w-full h-14 text-lg bg-primary hover:bg-primary/90" data-testid="send-notif-btn">
          {sending ? <Loader2 className="w-5 h-5 animate-spin ml-2" /> : <Send className="w-5 h-5 ml-2" />}
          {sending ? 'جارٍ الإرسال...' : 'إرسال الإشعار'}
        </Button>
      </Card>

      {/* Notification Log */}
      <div className="max-w-2xl mx-auto" data-testid="notification-log">
        <h2 className="text-2xl font-bold text-accent mb-4 flex items-center gap-2">
          <History className="w-6 h-6 text-primary" /> سجل الإشعارات المرسلة
        </h2>
        {loadingLog ? (
          <div className="text-center py-8"><Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" /></div>
        ) : log.length === 0 ? (
          <Card className="p-8 text-center text-muted-foreground">لا توجد إشعارات مرسلة بعد</Card>
        ) : (
          <div className="space-y-3">
            {log.map((entry) => {
              const readPct = entry.total > 0 ? Math.round((entry.read_count / entry.total) * 100) : 0;
              return (
                <Card key={entry.notification_id} className="p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-accent truncate">{entry.title}</h3>
                      <p className="text-sm text-muted-foreground line-clamp-1 mt-0.5">{entry.message}</p>
                      <p className="text-xs text-muted-foreground mt-2">
                        {new Date(entry.created_at).toLocaleString('ar', { dateStyle: 'medium', timeStyle: 'short' })}
                      </p>
                    </div>
                    <div className="shrink-0 text-left space-y-1">
                      <div className="flex items-center gap-1.5 text-sm">
                        <Users className="w-3.5 h-3.5 text-primary" />
                        <span className="font-semibold">{entry.total}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-sm">
                        <Eye className="w-3.5 h-3.5 text-green-600" />
                        <span className="text-green-600 font-semibold">{entry.read_count}</span>
                      </div>
                      <div className="w-16 bg-gray-100 rounded-full h-1.5 mt-1">
                        <div className="bg-green-500 h-1.5 rounded-full transition-all" style={{ width: `${readPct}%` }} />
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminNotificationsPage;
